import { ref, watch, nextTick, onBeforeUnmount } from 'vue'
import * as echarts from 'echarts'
import { solveCubicCoefficients, getCurveValue } from '../utils/cubicCurve.js'

// ECharts 曲线图：渲染、锚点拖拽（1/3、2/3 点）、上下分割条拖拽
// 外部依赖（均为 Ref）：
//   servos            - 普通舵机数组
//   dualServos        - 双舵机数组（仅用于触发重绘，不进图表）
//   trajectoryConfigs - 轨迹配置数组
export function useChart({ servos, dualServos, trajectoryConfigs }) {
  const chartRef = ref(null)
  const showTrajectoryChart = ref(false) // 控制整个轨迹图显示/隐藏（默认关闭）
  let chartInstance = null

  // 每个舵机名称对应的图表点元数据（也被舵机重名判定复用）
  let pointMetaMap = {}
  let pointMetaMapIdx = {}

  function getChartSeries() {
    pointMetaMap = {}
    // 收集所有舵机数据：单舵机 + 所有轨迹配置的舵机
    // 注意：dualServos仅用于机械臂可视化，不应该重复添加到图表中
    const allTrajectoryServos = trajectoryConfigs.value.flatMap(t => t.servos || [])
    const allServosData = [...servos.value, ...allTrajectoryServos]
    return allServosData.map((servo, sIdx) => {
      let points = []
      let step = 0
      let pointMeta = []
      servo.m.forEach((motion, mIdx) => {
        // 跳过无效的 motion 对象
        if (!motion || motion.steps === undefined) return
        // 取四点
        const steps = motion.steps
        const start = motion.param[0]
        const end = motion.param[1]
        // 默认值处理
        if (motion.param[2] === undefined) motion.param[2] = Number((start + (end - start) / 3).toFixed(1))
        if (motion.param[3] === undefined) motion.param[3] = Number((start + 2 * (end - start) / 3).toFixed(1))
        const y1 = motion.param[2]
        const y2 = motion.param[3]
        // 四点坐标
        const x0 = 0, y0 = start
        const x1 = steps / 3, x2 = 2 * steps / 3
        const x3 = steps, y3 = end
        const coeffs = solveCubicCoefficients(x0, y0, x1, y1, x2, y2, x3, y3)
        const isLastMotion = mIdx === servo.m.length - 1
        const loopEnd = isLastMotion ? steps : steps - 1
        for (let i = 0; i <= loopEnd; i++) {
          const val = getCurveValue(coeffs, i)
          points.push([step, val])
          pointMeta.push({sIdx, mIdx, i, steps: motion.steps})
          step++
        }
      })

      pointMetaMap[servo.name] = pointMeta
      return {
        name: servo.name,
        type: 'line',
        data: points,
        showSymbol: true,
        symbolSize: 1,
        emphasis: {focus: 'series'},
        universalTransition: true,
        draggable: true
      }
    })
  }

  function renderChart() {
    nextTick(() => {
      if (!chartRef.value || !showTrajectoryChart.value) return
      if (!chartInstance) {
        chartInstance = echarts.init(chartRef.value)
      }
      const series = getChartSeries()

      const option = {
        tooltip: {trigger: 'axis'},
        legend: {},
        xAxis: {
          type: 'value',
          name: '步数(steps)',
          splitLine: {show: false},
          axisLabel: {formatter: value => value}
        },
        yAxis: {
          type: 'value',
          name: '位置',
          splitLine: {show: false},
          axisLabel: {formatter: value => Math.round(value)}
        },
        series
      }
      // 不要用 clear，直接 setOption(option, true)
      chartInstance.setOption(option, true)

      setTimeout(() => {
        const graphics = []
        // 收集所有舵机数据：单舵机 + 所有轨迹配置的舵机
        // 注意：dualServos仅用于机械臂可视化，不应该重复添加到图表中
        const allTrajectoryServos = trajectoryConfigs.value.flatMap(t => t.servos || [])
        const allServosData = [...servos.value, ...allTrajectoryServos]
        series.forEach((ser, sIdx) => {
          ser.data.forEach((pt, ptIdx) => {
            const meta = pointMetaMap[allServosData[sIdx].name][ptIdx]
            let isDraggable = false
            if (meta) {
              const servo = allServosData[sIdx]
              const motion = servo.m[meta.mIdx]
              if (!motion || motion.steps === undefined) {
                // 跳过无效的 motion 对象
                return
              }
              const steps = motion.steps
              const thirdStep = Math.round(steps / 3)
              const twoThirdStep = Math.round(2 * steps / 3)
              if (meta.i === thirdStep || meta.i === twoThirdStep) {
                isDraggable = true
              }
            }
            graphics.push({
              type: 'circle',
              position: chartInstance.convertToPixel({xAxisIndex: 0, yAxisIndex: 0}, pt),
              shape: {r: 2},
              invisible: false,
              draggable: isDraggable,
              z: 100,
              style: {fill: isDraggable ? '#ff0000' : '#fff', stroke: ser.color || '#000', lineWidth: 2},
              ondrag: isDraggable ? function (event) {
                const curPos = this.position
                this.position = [curPos[0], event.offsetY]
                const pos = chartInstance.convertFromPixel({xAxisIndex: 0, yAxisIndex: 0}, this.position)
                if (!pos) return
                const [newX, newYRaw] = pos
                const newY = Number(newYRaw.toFixed(1))
                const meta = pointMetaMap[allServosData[sIdx].name][ptIdx]
                if (!meta) return
                const {mIdx, i, steps} = meta
                const servo = allServosData[sIdx]
                const motion = servo.m[mIdx]
                if (!motion || motion.steps === undefined) return
                if (i === Math.round(steps / 3)) {
                  motion.param[2] = newY
                } else if (i === Math.round(2 * steps / 3)) {
                  motion.param[3] = newY
                }
                // 拖动后主图和锚点都要刷新
                renderChart()
              } : undefined
            })
          })
        })
        chartInstance.setOption({graphic: graphics}, false)
      }, 0)
    })
  }

  // 调整图表大小（视图切换/分割条拖拽后调用）
  function resizeChart() {
    if (chartInstance) {
      chartInstance.resize()
    }
  }

  // 为新增舵机分配不重名的显示名称（同名时追加序号）
  function resolveNewServoName(baseName) {
    let servoName = baseName
    let servoNameIdx = 0
    if (pointMetaMap.hasOwnProperty(baseName)) {
      if (pointMetaMapIdx.hasOwnProperty(baseName)) {
        servoNameIdx = pointMetaMapIdx[baseName] + 1
        pointMetaMapIdx[baseName] = servoNameIdx
      } else {
        pointMetaMapIdx[baseName] = 1
        servoNameIdx = 1
      }
      servoName = baseName + servoNameIdx
    }
    return { servoName, servoNameIdx }
  }

  // 数据变化时自动重绘
  watch(servos, () => {
    renderChart()
  }, {deep: true})

  watch(dualServos, () => {
    renderChart()
  }, {deep: true})

  // 监听轨迹配置变化，更新图表
  watch(trajectoryConfigs, () => {
    renderChart()
  }, {deep: true})

  // 上下面板分割条拖拽
  let isDragging = false
  let startY = 0
  let startHeight = 0

  function startDrag(e) {
    isDragging = true
    startY = e.clientY
    const topPanel = document.querySelector('.top-panel')
    startHeight = topPanel.offsetHeight
    document.body.style.cursor = 'row-resize'
    window.addEventListener('mousemove', onDrag)
    window.addEventListener('mouseup', stopDrag)
  }

  function onDrag(e) {
    if (!isDragging) return
    const container = document.querySelector('.split-container')
    const totalHeight = container.offsetHeight
    let newHeight = startHeight + (e.clientY - startY)
    newHeight = Math.max(100, Math.min(totalHeight - 100, newHeight))
    const percent = (newHeight / totalHeight) * 100
    document.querySelector('.top-panel').style.flexBasis = percent + '%'
    document.querySelector('.bottom-panel').style.flexBasis = (100 - percent) + '%'
    resizeChart()
  }

  function stopDrag() {
    isDragging = false
    document.body.style.cursor = ''
    window.removeEventListener('mousemove', onDrag)
    window.removeEventListener('mouseup', stopDrag)
  }

  onBeforeUnmount(() => {
    window.removeEventListener('mousemove', onDrag)
    window.removeEventListener('mouseup', stopDrag)
  })

  return {
    chartRef,
    showTrajectoryChart,
    renderChart,
    resizeChart,
    resolveNewServoName,
    startDrag
  }
}
